import ExcelJS from 'exceljs';

export function safeExcelSheetName(name) {
  const cleaned = String(name || 'Sheet').replace(/[:\\/?*[\]]/g, '_');
  return cleaned.slice(0, 31) || 'Sheet';
}

export function createWorkbook() {
  return new ExcelJS.Workbook();
}

function normalizeCellValue(value) {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'object') {
    if (value.text !== undefined) return value.text;
    if (value.richText) return value.richText.map((part) => part.text).join('');
    if (value.result !== undefined) return value.result;
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return value;
}

export function addJsonSheet(workbook, sheetName, rows) {
  const worksheet = workbook.addWorksheet(safeExcelSheetName(sheetName));
  const data = rows && rows.length ? rows : [{ _note: 'No data' }];
  const keys = Object.keys(data[0]);
  worksheet.columns = keys.map((key) => ({ header: key, key }));
  data.forEach((row) => {
    worksheet.addRow(
      Object.fromEntries(
        keys.map((key) => [key, normalizeCellValue(row[key])])
      )
    );
  });
  return worksheet;
}

export async function downloadWorkbook(workbook, filename) {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

// Parse quoted CSV fields, including question/option text containing newlines.
function parseCsvText(text, includeRowNumbers) {
  const records = [];
  let values = [], value = '', inQuotes = false, line = 1, rowStart = 1;
  const pushRecord = () => {
    values.push(value.trim());
    if (values.some((cell) => cell !== '')) records.push({ values, row: rowStart });
    values = []; value = '';
  };
  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    if (char === '"') {
      if (inQuotes && text[i + 1] === '"') { value += '"'; i += 1; }
      else inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) { values.push(value.trim()); value = ''; }
    else if (char === '\r' || char === '\n') {
      if (char === '\r' && text[i + 1] === '\n') i += 1;
      if (inQuotes) value += '\n';
      else pushRecord();
      line += 1;
      if (!inQuotes) rowStart = line;
    } else value += char;
  }
  if (inQuotes) throw new Error('The CSV contains an unclosed quoted field. Please correct it and upload again.');
  pushRecord();
  if (!records.length) return [];
  const headers = records[0].values.map((header) => header.replace(/^\uFEFF/, '').trim());
  return records.slice(1).map((record) => ({
    ...Object.fromEntries(headers.map((header, index) => [header, record.values[index] ?? ''])),
    ...(includeRowNumbers ? { __rowNumber: record.row } : {}),
  }));
}

export async function readSpreadsheetRowsFromFile(file, { includeRowNumbers = false } = {}) {
  const arrayBuffer = await file.arrayBuffer();
  const extension = `.${file.name.split('.').pop()?.toLowerCase() || ''}`;
  if (extension === '.csv') {
    const rows = parseCsvText(new TextDecoder().decode(arrayBuffer), includeRowNumbers);
    if (!rows.length) throw new Error('The file is empty');
    return rows;
  }
  if (extension !== '.xlsx') throw new Error('Please upload an .xlsx or .csv file.');

  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(arrayBuffer);

  if (!workbook.worksheets.length) {
    throw new Error('The file contains no sheets');
  }

  const worksheet = workbook.worksheets[0];
  const headerRow = worksheet.getRow(1);
  const headers = {};
  headerRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    if (cell.value != null && cell.value !== '') {
      headers[colNumber] = String(normalizeCellValue(cell.value)).trim();
    }
  });

  const rows = [];
  for (let rowNumber = 2; rowNumber <= worksheet.rowCount; rowNumber += 1) {
    const row = worksheet.getRow(rowNumber);
    const record = {};
    let hasValue = false;
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      const key = headers[colNumber];
      if (!key) return;
      const value = normalizeCellValue(cell.value);
      record[key] = value;
      if (value !== '' && value != null) {
        hasValue = true;
      }
    });
    if (hasValue) rows.push(includeRowNumbers ? { ...record, __rowNumber: rowNumber } : record);
  }

  if (!rows.length) {
    throw new Error('The file is empty');
  }

  return rows;
}
